import React from 'react';
import PropTypes from 'prop-types';
import { is, has, both, equals } from 'ramda';
import { Grid, Row, Col } from 'react-flexbox-grid';

import Dialog from 'material-ui/Dialog';
import Paper from 'material-ui/Paper';
import FlatButton from 'material-ui/FlatButton';
import RaisedButton from 'material-ui/RaisedButton';
import TextField from 'components/MaterialTextField';
import Checkbox from 'material-ui/Checkbox';

import RefreshIndicator from 'material-ui/RefreshIndicator';
import VirtualList from 'react-tiny-virtual-list';

import MobileDialog from './mobileDialog';

import { tableRow, tableCell, selected, cellName, cellDetail } from './MedicationSearchDialog.less';
import { formatStrength } from '../../formatters';

const isSelected = (selection, medIndex) =>
  both(is(Object), has('medIndex'))(selection) && equals(medIndex, selection.medIndex);

const loadingIndicator = () => (
  <RefreshIndicator
    color={'048b82'}
    size={30}
    left={30}
    top={5}
    status="loading"
    style={{ display: 'inline-block', position: 'relative' }}
  />
);

const isLoading = load => (load ? loadingIndicator() : null);

const isMobile = viewPortSize => {
  // Landscape
  if (viewPortSize.width > viewPortSize.height && viewPortSize.height <= 500) return true;
  // Potrait
  if (viewPortSize.width < viewPortSize.height && viewPortSize.width <= 600) return true;
  return false;
};

const MedicationSearchDialog = ({
  open,
  selection,
  results,
  onSearch,
  onSelect,
  onChoose,
  onClose,
  localeMessage,
  resultsLoad,
  viewPortSize,
}) =>
  !isMobile(viewPortSize) ? (
    <Dialog
      title={false}
      open={open}
      contentStyle={{ width: '80%', maxWidth: '1000px', minHeight: 350 }}
      bodyStyle={{ minHeight: 350 }}
      style={{ minHeight: 350 }}
      autoDetectWindowHeight={false}
      actions={[
        <FlatButton label={localeMessage('medication.dialog.cancel')} onClick={onClose} />,
        <RaisedButton
          label={localeMessage('medication.dialog.choose')}
          onClick={onChoose}
          primary
          style={{ marginLeft: '0.5rem' }}
        />,
      ]}
      autoScrollBodyContent
      modal
    >
      <Grid fluid>
        <Row style={{ marginBottom: '2rem' }}>
          <Col xs>
            <h5>{localeMessage('medication.labels.chooseMedication')}</h5>
          </Col>
        </Row>
        <Row style={{ marginBottom: '2rem' }} middle="xs">
          <Col xs={10}>
            <Paper style={{ padding: '0.5rem 1rem' }}>
              <Row>
                <Col xs>
                  <TextField
                    fullWidth
                    hintText={localeMessage('medication.dialog.search')}
                    onChange={(e, v) => onSearch(v)}
                  />
                </Col>
              </Row>
            </Paper>
          </Col>
          <Col xs={2}>{isLoading(resultsLoad)}</Col>
        </Row>
        <Row>
          <Col xs>
            <Paper style={{ padding: '0.5rem' }}>
              <VirtualList
                width="100%"
                height={400}
                itemCount={results.length}
                itemSize={75}
                renderItem={({ index, style }) => (
                  <Row
                    key={results[index].medIndex}
                    middle="xs"
                    className={`${tableRow} ${isSelected(selection, results[index].medIndex) ? selected : ''}`}
                    style={style}
                    onClick={() => onSelect(index)}
                  >
                    <Col className={tableCell} xs={1}>
                      <Row start="xs" middle="xs">
                        <Checkbox
                          checked={isSelected(selection, results[index].medIndex)}
                          style={{ width: '50%', margin: '0px auto' }}
                        />
                      </Row>
                    </Col>
                    <Col className={tableCell} xs={11}>
                      <Row>
                        <p className={cellName}>
                          {results[index].name}
                          {formatStrength(results[index]) ? ', ' + formatStrength(results[index]) : null}
                        </p>
                      </Row>
                      <Row>
                        <p className={cellDetail}>{results[index].dosageForm}</p>
                      </Row>
                      <Row>
                        <p className={cellDetail}>{results[index].substanceString}</p>
                      </Row>
                    </Col>
                  </Row>
                )}
              />
            </Paper>
          </Col>
        </Row>
      </Grid>
    </Dialog>
  ) : (
    <MobileDialog
      open={open}
      onSearch={onSearch}
      onSelect={onSelect}
      onChoose={onChoose}
      onClose={onClose}
      localeMessage={localeMessage}
      results={results}
      selection={selection}
      resultsLoad={resultsLoad}
      viewPortSize={viewPortSize}
    />
  );

MedicationSearchDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onSearch: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onChoose: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  localeMessage: PropTypes.func.isRequired,
  results: PropTypes.array,
  selection: PropTypes.object,
  resultsLoad: PropTypes.bool.isRequired,
  viewPortSize: PropTypes.object,
};

export default MedicationSearchDialog;
